<template>
  <v-card class="mb-4" outlined>
    <v-card-text>
      <div class="d-flex justify-space-between align-center">
        <div class="flex-grow-1">
          <div class="text-subtitle-1">
            {{ oldGame.home_team_id }} vs {{ oldGame.away_team_id }}
          </div>
          <div class="text-caption grey--text">
            {{ formatDate(oldGame.starttimestamp) }}
          </div>
        </div>
        <div class="mr-3 d-flex flex-column align-center justify-center">
          <v-btn size="small" v-if="!oldGame.new_game_id" small @click="saveNewGame">
            Tallenna uusi peli
          </v-btn>
          <v-btn v-if="!oldGame.events_saved && oldGame.new_game_id" size="small" class="mt-1" small @click="saveEvents">
            Luo eventit
          </v-btn>
        </div>
        <div>
          <v-btn v-if="oldGame.new_game_id" v-tooltip="'Poista peli'" x-small @click="deleteGame()" color="error">
            <v-icon size="15">mdi-delete-outline</v-icon>
          </v-btn>
        </div>
        <div class="d-flex flex-column align-center justify-center" style="height: 100%; width: 200px; text-align: center;">
          <v-chip
            small
            class="mt-1"
            color="primary"
            label
            v-tooltip="{
              content: JSON.stringify(oldGame, null, 2),
              maxWidth: 400
            }"
          >
            <v-icon size="15" left>mdi-database</v-icon>
            Vanha
          </v-chip>
          <v-chip
            small
            class="mt-1"
            color="success"
            label
            v-tooltip="{
              content: JSON.stringify(transformOldToNewGame, null, 2),
              maxWidth: 400
            }"
          >
            <v-icon size="15" left>mdi-database-import</v-icon>
            Uusi
          </v-chip>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import a from '@/utils/axios/axios'
import { toCloudflareHlsUrl } from '@/utils/video/hlsUrls'

export default {
  props: ['oldGame'],
  data: () => ({
    mappedLeagues: {
      'msu': 1,
      'nsu': 2,
      'myp': 3
    },
    mappedNewLeagues: {
      'msu': 8,
      'nsu': 9,
      'myp': 10
    },
    mappedSeasons: {
      'msu': 7,
      'nsu': 9,
      'myp': 11
    },
    mapped_team_ids: { // epesis2id: epesis3id
      1002: 50,
      1003: 55,
      1004: 56,
      1005: 49,
      1006: 53,
      1007: 102,
      1008: 57,
      1009: 60,
      1010: 108,
      1011: 54,
      1012: 51,
      1013: 58,
      1015: 59,
      1016: 52,
      2001: 64,
      2002: 72,
      2003: 63,
      2005: 67,
      2006: 62,
      2007: 61,
      2010: 69,
      2011: 65,
      2013: 71,
      2014: 70,
      2015: 68,
      2016: 103,
      2017: 97,
      2018: 66,
      3004: 74,
      3005: 83,
      3008: 107,
      3009: 79,
      3010: 81,
      3014: 73,
      3015: 76,
      3016: 75,
      3017: 80,
      3019: 104,
      3020: 84,
      3021: 77,
    },
    mapped_analysis_state: {
      msu: 14,
      nsu: 15,
      myp: 16
    },
  }),
  computed: {
    transformOldToNewGame() {
      // 1) Find the leagueKey by matching this.oldGame.league_id to mappedLeagues
      //    If league_id is not 1, 2, or 3, we'll get undefined
      const leagueKey = Object.keys(this.mappedLeagues).find(
        key => this.mappedLeagues[key] === this.oldGame.league_id
      );

      // 2) season_id is mapped from leagueKey => mappedNewLeagues
      //    If league_id is not in (1,2,3), leagueKey is undefined => season_id = null
      const newSeasonId = leagueKey ? this.mappedSeasons[leagueKey] : null;
      const newLeagueId = leagueKey ? this.mappedNewLeagues[leagueKey] : null;

      // 3) done_analysis_state is also derived from leagueKey => mapped_analysis_state
      const doneAnalysisState = leagueKey ? this.mapped_analysis_state[leagueKey] : null;

      // 4) home_team_id and away_team_id mapping
      const newHomeTeamId = this.mapped_team_ids[this.oldGame.home_team_id] || null;
      const newAwayTeamId = this.mapped_team_ids[this.oldGame.away_team_id] || null;

      // 5) Shown_live is always false (as per your clarification)
      const shownLive = false;

      // 6) starttime_unix should be in milliseconds (this.oldGame.starttimestamp is in seconds)
      //    If it's already in seconds, we multiply by 1000
      const starttimeUnix = this.oldGame.starttimestamp
        ? this.oldGame.starttimestamp * 1000
        : null;

      // 7) publishtime_unix => three hours after starttime_unix (in ms)
      //    3 hours = 3 * 60 * 60 * 1000 = 10800000
      const publishtimeUnix = starttimeUnix
        ? starttimeUnix + (3 * 60 * 60 * 1000)
        : null;

      // 8) video_url = identical to source_url
      //    video_live_url = replace "vod.m3u8" with "live.m3u8" (or "vod" with "live")
      const videoUrl = toCloudflareHlsUrl(this.oldGame.source_url || '');
      const videoLiveUrl = videoUrl.replace('vod.m3u8', 'live.m3u8');

      // 9) external_service_id = pesis_id
      const externalServiceId = this.oldGame.pesis_id != null ? this.oldGame.pesis_id : null;

      // 10) Score mapping
      const scoreHome = this.oldGame.home_points || 0;
      const scoreAway = this.oldGame.away_points || 0;

      // 11) will_be_analyzed => true if this.oldGame.wont_be_analyzed === 0
      const willBeAnalyzed = !this.oldGame.wont_be_analyzed;
      const analyzed = willBeAnalyzed;

      // 12) game_info & game_error = '' by default
      const gameInfo = '';
      const gameError = '';

      // 13) video_type always 'hls'
      const videoType = 'hls';

      // Construct the new object
      const newGame = {
        home_club_id: newHomeTeamId,
        away_club_id: newAwayTeamId,
        shown_live: shownLive,
        game_info: gameInfo,
        game_error: gameError,
        score_home: scoreHome,
        score_away: scoreAway,
        will_be_analyzed: willBeAnalyzed,
        starttime_unix: starttimeUnix,
        publishtime_unix: publishtimeUnix,
        video_url: videoUrl,
        video_live_url: videoLiveUrl,
        video_type: videoType,
        external_service_id: externalServiceId,
        season_id: newSeasonId,
        done_analysis_state: doneAnalysisState,
        league_id: newLeagueId,
        analyzed
      };

      return newGame;
    }
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp * 1000);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}. ${hours}:${minutes}`;
    },
    saveNewGame() {
      const newGame = this.transformOldToNewGame;
      if(!newGame.home_club_id || !newGame.away_club_id) {
        alert('Pelin koti- ja vastapuolijoiden tunnistaminen epäonnistui');
        console.log(newGame);
        console.log(this.oldGame);
        return;
      }
      a.post('/import/game', { new_game: newGame, old_game: this.oldGame })
        .then((e) => {
          this.$emit('saved', { old_game_id: this.oldGame.id, new_game_id: e.data.new_game_id })
          this.$store.dispatch('noti/success', 'Peli tallennettu onnistuneesti');
        })
        .catch(e => {
          this.$store.dispatch('noti/error', e);
        });
    },
    saveEvents() {
      a.put(`/import/events/${this.oldGame.id}/${this.oldGame.new_game_id}`)
        .then(() => {
          this.$emit('events_saved', { old_game_id: this.oldGame.id })
          this.$store.dispatch('noti/success', 'Eventit tallennettu onnistuneesti');
        })
        .catch(e => {
          this.$store.dispatch('noti/error', e);
        });
    },
    deleteGame() {
      a.delete(`/import/game/${this.oldGame.id}`)
        .then(() => {
          this.$emit('game_deleted', { old_game_id: this.oldGame.id })
          this.$store.dispatch('noti/success', 'Peli poistettu onnistuneesti');
        })
        .catch(e => {
          this.$store.dispatch('noti/error', e);
        });
    }
  }
}
</script>

<style scoped>
.v-card {
  transition: all 0.3s ease;
}
.v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}
</style>